"use client";

import { memo, useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Smile } from "lucide-react";
import { cn } from "@/lib/utils";

const EMOJI_STORAGE_KEY = "inbox:recent-emojis";
const MAX_RECENT_EMOJIS = 24;
const PICKER_WIDTH = 320;
const PICKER_GAP = 12;

// Common emojis for quick access
const EMOJI_CATEGORIES = {
	smileys: [
		"😀",
		"😃",
		"😄",
		"😁",
		"😆",
		"😅",
		"😂",
		"🤣",
		"😊",
		"😇",
		"🙂",
		"🙃",
		"😉",
		"😌",
		"😍",
		"🥰",
		"😘",
		"😗",
		"😙",
		"😚",
		"😋",
		"😛",
		"😝",
		"😜",
		"🤪",
		"🤨",
		"🧐",
		"🤓",
		"😎",
		"🤩",
		"🥳",
		"😏",
		"😒",
		"🙄",
		"😬",
		"🤗",
		"🤔",
		"🤭",
		"🤫",
		"🤥",
		"😶",
		"😐",
		"😑",
		"😮",
		"😯",
		"😲",
		"😴",
		"🤤",
		"😪",
		"😵",
		"🤐",
		"🤢",
		"🤮",
		"🤧",
		"🥵",
		"🥶",
		"😷",
		"🤒",
		"🤕",
	],
	gestures: [
		"👋",
		"🤚",
		"🖐",
		"✋",
		"🖖",
		"👌",
		"🤌",
		"🤏",
		"✌️",
		"🤞",
		"🤟",
		"🤘",
		"🤙",
		"👈",
		"👉",
		"👆",
		"🖕",
		"👇",
		"☝️",
		"👍",
		"👎",
		"✊",
		"👊",
		"🤛",
		"🤜",
		"👏",
		"🙌",
		"👐",
		"🤲",
		"🤝",
		"🙏",
		"✍️",
		"💪",
		"🦾",
		"🦿",
		"🦵",
		"🦶",
		"👂",
		"🦻",
		"👃",
	],
	hearts: [
		"❤️",
		"🧡",
		"💛",
		"💚",
		"💙",
		"💜",
		"🖤",
		"🤍",
		"🤎",
		"💔",
		"❤️‍🔥",
		"💕",
		"💞",
		"💓",
		"💗",
		"💖",
		"💘",
		"💝",
		"💟",
		"☮️",
		"✝️",
		"☪️",
		"🕉️",
		"☸️",
		"✡️",
		"🔯",
		"🕎",
		"☯️",
		"☦️",
		"🛐",
		"⛎",
		"♈",
		"♉",
		"♊",
		"♋",
		"♌",
		"♍",
		"♎",
		"♏",
		"♐",
		"♑",
		"♒",
		"♓",
		"🆔",
		"⚛️",
	],
	objects: [
		"⌚",
		"📱",
		"📲",
		"💻",
		"⌨️",
		"🖥️",
		"🖨️",
		"🖱️",
		"🖲️",
		"🕹️",
		"🗜️",
		"💾",
		"💿",
		"📀",
		"📼",
		"📷",
		"📸",
		"📹",
		"🎥",
		"📽️",
		"🎞️",
		"📞",
		"☎️",
		"📟",
		"📠",
		"📺",
		"📻",
		"🎙️",
		"🎚️",
		"🎛️",
		"⏱️",
		"⏲️",
		"⏰",
		"🕰️",
		"⌛",
		"⏳",
		"📡",
		"🔋",
		"🔌",
		"💡",
		"🔦",
		"🕯️",
		"🧯",
		"🛢️",
		"💸",
		"💵",
		"💴",
		"💶",
		"💷",
		"💰",
		"💳",
		"💎",
		"⚖️",
		"🪜",
		"🧰",
		"🪛",
		"🔧",
		"🔨",
		"⚒️",
		"🛠️",
		"⛏️",
		"🪚",
		"🔩",
		"⚙️",
		"🪤",
		"🧱",
		"⛓️",
		"🧲",
		"🔫",
		"💣",
		"🧨",
		"🪓",
		"🔪",
		"🗡️",
		"⚔️",
		"🛡️",
		"🚬",
		"⚰️",
		"🪦",
		"⚱️",
		"🏺",
		"🔮",
		"📿",
		"🧿",
		"🪬",
		"💈",
		"⚗️",
		"🔭",
		"🔬",
		"🕳️",
		"🩹",
		"🩺",
		"💊",
		"💉",
		"🩸",
		"🧬",
		"🦠",
		"🧫",
		"🧪",
		"🌡️",
		"🧹",
		"🪠",
		"🧺",
		"🧻",
		"🚽",
		"🚿",
		"🛁",
		"🛀",
		"🧼",
		"🪥",
		"🪒",
		"🧽",
		"🪣",
		"🧴",
		"🛎️",
		"🔑",
		"🗝️",
		"🚪",
		"🪑",
		"🛋️",
		"🛏️",
		"🛌",
		"🧸",
		"🪆",
		"🖼️",
		"🪞",
		"🪟",
		"🛍️",
		"🛒",
		"🎁",
		"🎈",
		"🎏",
		"🪄",
		"🪅",
		"🎀",
		"🪡",
		"🪢",
		"🧶",
		"🪮",
		"🪭",
		"👓",
		"🕶️",
		"🥽",
		"🥼",
		"🦺",
		"👔",
		"👕",
		"👖",
		"🧣",
		"🧤",
		"🧥",
		"🧦",
		"👗",
		"👘",
		"🥻",
		"🩱",
		"🩲",
		"🩳",
		"👙",
		"👚",
		"👛",
		"👜",
		"👝",
		"🛍️",
		"🎒",
		"👞",
		"👟",
		"🥾",
		"🥿",
		"👠",
		"👡",
		"🩰",
		"👢",
		"🪮",
		"👑",
		"👒",
		"🎩",
		"🎓",
		"🧢",
		"🪖",
		"⛑️",
		"📿",
		"💄",
		"💍",
		"💎",
		"🔇",
		"🔈",
		"🔉",
		"🔊",
		"📢",
		"📣",
		"📯",
		"🔔",
		"🔕",
		"🎼",
		"🎵",
		"🎶",
		"🎙️",
		"🎚️",
		"🎛️",
		"🎤",
		"🎧",
		"📻",
		"🎷",
		"🪗",
		"🎹",
		"🎸",
		"🪕",
		"🎺",
		"🎻",
		"🪘",
		"🥁",
		"🪇",
		"🪈",
		"📱",
		"📲",
		"☎️",
		"📞",
		"📟",
		"📠",
		"🔋",
		"🔌",
		"💻",
		"🖥️",
		"🖨️",
		"⌨️",
		"🖱️",
		"🖲️",
		"🕹️",
		"🗜️",
		"💾",
		"💿",
		"📀",
		"📼",
		"📷",
		"📸",
		"📹",
		"🎥",
		"📽️",
		"🎞️",
		"📞",
		"☎️",
		"📟",
		"📠",
		"📺",
		"📻",
		"🎙️",
		"🎚️",
		"🎛️",
		"⏱️",
		"⏲️",
		"⏰",
		"🕰️",
		"⌛",
		"⏳",
		"📡",
	],
	animals: [
		"🐶",
		"🐱",
		"🐭",
		"🐹",
		"🐰",
		"🦊",
		"🐻",
		"🐼",
		"🐨",
		"🐯",
		"🦁",
		"🐮",
		"🐷",
		"🐽",
		"🐸",
		"🐵",
		"🙈",
		"🙉",
		"🙊",
		"🐒",
		"🐔",
		"🐧",
		"🐦",
		"🐤",
		"🐣",
		"🐥",
		"🦆",
		"🦅",
		"🦉",
		"🦇",
		"🐺",
		"🐗",
		"🐴",
		"🦄",
		"🐝",
		"🐛",
		"🦋",
		"🐌",
		"🐞",
		"🐜",
		"🪰",
		"🪱",
		"🪲",
		"🦟",
		"🦗",
		"🕷️",
		"🦂",
		"🐢",
		"🐍",
		"🦎",
		"🦖",
		"🦕",
		"🐙",
		"🦑",
		"🦐",
		"🦞",
		"🦀",
		"🐡",
		"🐠",
		"🐟",
		"🐬",
		"🐳",
		"🐋",
		"🦈",
		"🐊",
		"🐅",
		"🐆",
		"🦓",
		"🦍",
		"🦧",
		"🦣",
		"🐘",
		"🦛",
		"🦏",
		"🐪",
		"🐫",
		"🦒",
		"🦘",
		"🦬",
		"🐃",
		"🐂",
		"🐄",
		"🐎",
		"🐖",
		"🐏",
		"🐑",
		"🦙",
		"🐐",
		"🦌",
		"🐕",
		"🐩",
		"🦮",
		"🐕‍🦺",
		"🐈",
		"🐈‍⬛",
		"🪶",
		"🦅",
		"🦆",
		"🦢",
		"🦩",
		"🦚",
		"🦜",
		"🐓",
		"🦃",
		"🦤",
		"🪿",
		"🦉",
		"🦅",
		"🦆",
		"🦢",
		"🦩",
		"🦚",
		"🦜",
		"🐓",
		"🦃",
		"🦤",
		"🪿",
	],
	food: [
		"🍏",
		"🍎",
		"🍐",
		"🍊",
		"🍋",
		"🍌",
		"🍉",
		"🍇",
		"🍓",
		"🍈",
		"🍒",
		"🍑",
		"🥭",
		"🍍",
		"🥥",
		"🥝",
		"🍅",
		"🍆",
		"🥑",
		"🥦",
		"🥬",
		"🥒",
		"🌶️",
		"🌽",
		"🥕",
		"🥔",
		"🍠",
		"🥐",
		"🥯",
		"🍞",
		"🥖",
		"🥨",
		"🧀",
		"🥚",
		"🍳",
		"🥞",
		"🥓",
		"🥩",
		"🍗",
		"🍖",
		"🦴",
		"🌭",
		"🍔",
		"🍟",
		"🍕",
		"🥪",
		"🥙",
		"🧆",
		"🌮",
		"🌯",
		"🥗",
		"🥘",
		"🥫",
		"🍝",
		"🍜",
		"🍲",
		"🍛",
		"🍣",
		"🍱",
		"🥟",
		"🦪",
		"🍤",
		"🍙",
		"🍚",
		"🍘",
		"🍥",
		"🥠",
		"🥮",
		"🍢",
		"🍡",
		"🍧",
		"🍨",
		"🍦",
		"🥧",
		"🧁",
		"🍰",
		"🎂",
		"🍮",
		"🍭",
		"🍬",
		"🍫",
		"🍿",
		"🍩",
		"🍪",
		"🌰",
		"🥜",
		"🍯",
		"🥛",
		"🍼",
		"🫖",
		"☕️",
		"🍵",
		"🧃",
		"🥤",
		"🧋",
		"🍶",
		"🍺",
		"🍻",
		"🥂",
		"🍷",
		"🥃",
		"🍸",
		"🍹",
		"🧉",
		"🍾",
		"🧊",
	],
	activities: [
		"⚽",
		"🏀",
		"🏈",
		"⚾",
		"🥎",
		"🎾",
		"🏐",
		"🏉",
		"🥏",
		"🎱",
		"🏓",
		"🏸",
		"🏒",
		"🏑",
		"🥍",
		"🏏",
		"🥅",
		"⛳",
		"🏹",
		"🎣",
		"🥊",
		"🥋",
		"🎽",
		"🛹",
		"🛷",
		"⛸️",
		"🥌",
		"🎿",
		"⛷️",
		"🏂",
		"🪂",
		"🏋️",
		"🤼",
		"🤸",
		"🤺",
		"⛹️",
		"🤾",
		"🏌️",
		"🏇",
		"🧘",
		"🏄",
		"🏊",
		"🤽",
		"🚣",
		"🧗",
		"🚵",
		"🚴",
		"🏆",
		"🥇",
		"🥈",
		"🥉",
		"🏅",
		"🎖️",
		"🏵️",
		"🎗️",
		"🎫",
		"🎟️",
		"🎪",
		"🤹",
		"🎭",
		"🩰",
		"🎨",
		"🎬",
		"🎤",
		"🎧",
		"🎼",
		"🎹",
		"🥁",
		"🎷",
		"🎺",
		"🎸",
		"🪕",
		"🎻",
		"🎲",
		"♟️",
		"🎯",
		"🎳",
		"🎮",
		"🎰",
		"🧩",
	],
	travel: [
		"🚗",
		"🚕",
		"🚙",
		"🚌",
		"🚎",
		"🏎️",
		"🚓",
		"🚑",
		"🚒",
		"🚐",
		"🛻",
		"🚚",
		"🚛",
		"🚜",
		"🦯",
		"🦽",
		"🦼",
		"🛴",
		"🚲",
		"🛵",
		"🏍️",
		"🛺",
		"🚨",
		"🚔",
		"🚍",
		"🚘",
		"🚖",
		"🚡",
		"🚠",
		"🚟",
		"🚃",
		"🚋",
		"🚞",
		"🚝",
		"🚄",
		"🚅",
		"🚈",
		"🚂",
		"🚆",
		"🚇",
		"🚊",
		"🚉",
		"✈️",
		"🛫",
		"🛬",
		"🪂",
		"💺",
		"🚁",
		"🚟",
		"🚠",
		"🚡",
		"🛰️",
		"🚀",
		"🛸",
		"🛎️",
		"🧳",
		"⌛",
		"⏳",
		"⌚",
		"⏰",
		"⏲️",
		"⏱️",
		"🕛",
		"🕧",
		"🕐",
		"🕜",
		"🕑",
		"🕝",
		"🕒",
		"🕞",
		"🕓",
		"🕟",
		"🕔",
		"🕠",
		"🕕",
		"🕡",
		"🕖",
		"🕢",
		"🕗",
		"🕣",
		"🕘",
		"🕤",
		"🕙",
		"🕥",
		"🕚",
		"🕦",
	],
	symbols: [
		"❤️",
		"🧡",
		"💛",
		"💚",
		"💙",
		"💜",
		"🖤",
		"🤍",
		"🤎",
		"💔",
		"❣️",
		"💕",
		"💞",
		"💓",
		"💗",
		"💖",
		"💘",
		"💝",
		"💟",
		"☮️",
		"✝️",
		"☪️",
		"🕉️",
		"☸️",
		"✡️",
		"🔯",
		"🕎",
		"☯️",
		"☦️",
		"🛐",
		"⛎",
		"♈",
		"♉",
		"♊",
		"♋",
		"♌",
		"♍",
		"♎",
		"♏",
		"♐",
		"♑",
		"♒",
		"♓",
		"🆔",
		"⚛️",
		"🉑",
		"☢️",
		"☣️",
		"📴",
		"📳",
		"🈶",
		"🈚",
		"🈸",
		"🈺",
		"🈷️",
		"✴️",
		"🆚",
		"💮",
		"🉐",
		"㊙️",
		"㊗️",
		"🈴",
		"🈵",
		"🈹",
		"🈲",
		"🅰️",
		"🅱️",
		"🆎",
		"🆑",
		"🅾️",
		"🆘",
		"❌",
		"⭕",
		"🛑",
		"⛔",
		"📛",
		"🚫",
		"💯",
		"💢",
		"♨️",
		"🚷",
		"🚯",
		"🚳",
		"🚱",
		"🔞",
		"📵",
		"🚭",
		"❗",
		"❓",
		"❕",
		"❔",
		"‼️",
		"⁉️",
		"🔅",
		"🔆",
		"〽️",
		"⚠️",
		"🚸",
		"🔱",
		"⚜️",
		"🔰",
		"♻️",
		"✅",
		"🈯",
		"💹",
		"❇️",
		"✳️",
		"❎",
		"🌐",
		"💠",
		"Ⓜ️",
		"🌀",
		"💤",
		"🏧",
		"🚾",
		"♿",
		"🅿️",
		"🈳",
		"🈂️",
		"🛂",
		"🛃",
		"🛄",
		"🛅",
		"🚹",
		"🚺",
		"🚼",
		"🚻",
		"🚮",
		"🎦",
		"📶",
		"🈁",
		"🔣",
		"ℹ️",
		"🔤",
		"🔡",
		"🔠",
		"🆖",
		"🆗",
		"🆙",
		"🆒",
		"🆕",
		"🆓",
		"0️⃣",
		"1️⃣",
		"2️⃣",
		"3️⃣",
		"4️⃣",
		"5️⃣",
		"6️⃣",
		"7️⃣",
		"8️⃣",
		"9️⃣",
		"🔟",
		"🔢",
		"#️⃣",
		"*️⃣",
		"⏏️",
		"▶️",
		"⏸️",
		"⏯️",
		"⏹️",
		"⏺️",
		"⏭️",
		"⏮️",
		"⏩",
		"⏪",
		"⏫",
		"⏬",
		"◀️",
		"🔼",
		"🔽",
		"➡️",
		"⬅️",
		"⬆️",
		"⬇️",
		"↗️",
		"↘️",
		"↙️",
		"↖️",
		"↕️",
		"↔️",
		"↪️",
		"↩️",
		"⤴️",
		"⤵️",
		"🔀",
		"🔁",
		"🔂",
		"🔄",
		"🔃",
		"🎵",
		"🎶",
		"➕",
		"➖",
		"➗",
		"✖️",
		"💲",
		"💱",
		"™️",
		"©️",
		"®️",
		"〰️",
		"➰",
		"➿",
		"🔚",
		"🔙",
		"🔛",
		"🔜",
		"🔝",
		"〽️",
		"⚠️",
		"🚸",
		"🔱",
		"⚜️",
		"🔰",
		"♻️",
		"✅",
		"🈯",
		"💹",
		"❇️",
		"✳️",
		"❎",
		"🌐",
		"💠",
		"Ⓜ️",
		"🌀",
		"💤",
		"🏧",
		"🚾",
		"♿",
		"🅿️",
		"🈳",
		"🈂️",
		"🛂",
		"🛃",
		"🛄",
		"🛅",
		"🚹",
		"🚺",
		"🚼",
		"🚻",
		"🚮",
		"🎦",
		"📶",
		"🈁",
		"🔣",
		"ℹ️",
		"🔤",
		"🔡",
		"🔠",
		"🆖",
		"🆗",
		"🆙",
		"🆒",
		"🆕",
		"🆓",
	],
};

const ALL_EMOJIS = [
	...EMOJI_CATEGORIES.smileys,
	...EMOJI_CATEGORIES.gestures,
	...EMOJI_CATEGORIES.hearts,
	...EMOJI_CATEGORIES.objects,
	...EMOJI_CATEGORIES.animals,
	...EMOJI_CATEGORIES.food,
	...EMOJI_CATEGORIES.activities,
	...EMOJI_CATEGORIES.travel,
	...EMOJI_CATEGORIES.symbols,
];

type Props = {
	onEmojiSelect: (emoji: string) => void;
	className?: string;
	iconClassName?: string;
	disabled?: boolean;
};

function EmojiPicker({ onEmojiSelect, className, iconClassName, disabled = false }: Props) {
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState<{ left: number; bottom: number } | null>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [recentEmojis, setRecentEmojis] = useState<string[]>([]);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const pickerRef = useRef<HTMLDivElement | null>(null);

	// Load recent emojis from localStorage on mount
	useEffect(() => {
		if (typeof window !== "undefined") {
			try {
				const stored = localStorage.getItem(EMOJI_STORAGE_KEY);
				if (stored) {
					const parsed = JSON.parse(stored);
					if (Array.isArray(parsed)) {
						setRecentEmojis(parsed);
					}
				}
			} catch (error) {
				console.error("Failed to load recent emojis:", error);
			}
		}
	}, []);

	// Track emoji usage
	const handleEmojiSelect = (emoji: string) => {
		onEmojiSelect(emoji);
		setOpen(false);

		// Update recent emojis
		setRecentEmojis((prev) => {
			const updated = [emoji, ...prev.filter((e) => e !== emoji)].slice(0, MAX_RECENT_EMOJIS);

			// Save to localStorage
			if (typeof window !== "undefined") {
				try {
					localStorage.setItem(EMOJI_STORAGE_KEY, JSON.stringify(updated));
				} catch (error) {
					console.error("Failed to save recent emojis:", error);
				}
			}

			return updated;
		});
	};

	const filteredEmojis = useMemo(() => {
		if (searchQuery.length === 0) {
			// When not searching, exclude recent emojis from main list to avoid duplicates
			return ALL_EMOJIS.filter((emoji) => !recentEmojis.includes(emoji));
		}
		return ALL_EMOJIS.filter((emoji) => emoji.includes(searchQuery));
	}, [searchQuery, recentEmojis]);

	useEffect(() => {
		if (!open) return;

		const updatePosition = () => {
			const button = buttonRef.current;
			if (!button) return;

			const rect = button.getBoundingClientRect();
			const left = Math.min(
				Math.max(8, rect.right - PICKER_WIDTH),
				window.innerWidth - PICKER_WIDTH - 8,
			);

			setPosition({
				left,
				bottom: window.innerHeight - rect.top + PICKER_GAP,
			});
		};

		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target as Node;
			if (
				buttonRef.current?.contains(target) ||
				pickerRef.current?.contains(target)
			) {
				return;
			}
			setOpen(false);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false);
		};

		updatePosition();
		document.addEventListener("pointerdown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);

		return () => {
			document.removeEventListener("pointerdown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [open]);

	return (
		<>
			<button
				ref={buttonRef}
				type="button"
				disabled={disabled}
				className={cn(
					"inline-flex size-10 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-accent-foreground/10",
					disabled && "cursor-not-allowed opacity-50",
					className,
				)}
				aria-label="Add emoji"
				aria-expanded={open}
				onClick={() => {
					if (!disabled) setOpen((current) => !current);
				}}
			>
				<Smile className={cn("size-5 stroke-accent-foreground", iconClassName)} />
			</button>
			{open && position && typeof document !== "undefined" ? createPortal(
				<div
					ref={pickerRef}
					className="fixed z-[10000] max-h-80 w-80 max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border-0 bg-background px-4 pb-4 pt-5 shadow-xl"
					style={{ left: position.left, bottom: position.bottom }}
				>
				<div className="space-y-3 overflow-x-hidden">
					<input
						type="text"
						placeholder="Search emojis..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="w-full rounded-full border-0 bg-background px-3 py-2 text-sm shadow-sm outline-none focus:shadow-md focus:ring-2 focus:ring-secondary/20 focus-visible:ring-2 focus-visible:ring-secondary/20"
					/>
					{searchQuery.length === 0 && recentEmojis.length > 0 && (
						<div className="space-y-2">
							<div className="px-1 text-xs font-medium text-muted-foreground">
								Recent
							</div>
							<div className="grid w-full grid-cols-8 gap-2">
								{recentEmojis.map((emoji, index) => (
									<button
										key={`recent-${emoji}-${index}`}
										type="button"
										onClick={() => handleEmojiSelect(emoji)}
										className="flex size-8 items-center justify-center rounded-md text-lg transition-colors hover:bg-accent-foreground/10"
										aria-label={`Select emoji ${emoji}`}
									>
										{emoji}
									</button>
								))}
							</div>
						</div>
					)}
					<div className="max-h-56 overflow-y-auto overflow-x-hidden">
						{searchQuery.length > 0 && (
							<div className="mb-2 px-1 text-xs font-medium text-muted-foreground">
								Results
							</div>
						)}
						<div className="grid w-full grid-cols-8 gap-2">
							{filteredEmojis.map((emoji, index) => (
								<button
									key={`${emoji}-${index}`}
									type="button"
									onClick={() => handleEmojiSelect(emoji)}
									className="flex size-8 items-center justify-center rounded-md text-lg transition-colors hover:bg-accent-foreground/10"
									aria-label={`Select emoji ${emoji}`}
								>
									{emoji}
								</button>
							))}
						</div>
					</div>
				</div>
				</div>,
				document.body,
			) : null}
		</>
	);
}

export default memo(EmojiPicker);
